import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/v2/select";
import { DataTable } from "@/components/ui/v2/table/data-table";
import { TableToolbar } from "@/components/ui/v2/table/table-toolbar";
import { ROUTES } from "@/constants/routes";
import { ModuleName } from "@/features/integrations/utils/module-name";
import { encodeSlug } from "@/features/integrations/utils/slug";
import { useRecruitNowWebhookOverview } from "@/features/recruitnow/webhooks/api";
import type { RecruitNowWebhookOverviewItem } from "@/features/recruitnow/webhooks/api/types";
import { WebhookStatus } from "@/features/recruitnow/webhooks/api/types";
import { useWebhookOverviewTableColumns } from "@/features/recruitnow/webhooks/hooks/useWebhookOverviewTableColumns";
import { parseWebhookStatus } from "@/features/recruitnow/webhooks/utils/parseWebhookStatus";
import { useAuth } from "@/lib/hooks/useAuth";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";

export function WebhookOverviewContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenantId } = useAuth();
  const { orderBy, descending } = useSortSearchParams();
  const handleSort = useTableSortHandler();
  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const {
    searchValue,
    debouncedSearchValue,
    setSearchValue,
  } = useSearchSearchParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const clientType = searchParams.getAll("clientType");
  const statusParam = searchParams.get("status");
  const statusFilter = statusParam ? parseWebhookStatus(statusParam) : null;

  const { data, isLoading, isFetching } = useRecruitNowWebhookOverview({
    tenantId,
    clientType: clientType.length > 0 ? clientType : undefined,
    page,
    pageSize,
    orderBy: orderBy ?? undefined,
    descending: descending ?? undefined,
    status: statusFilter,
    search: debouncedSearchValue,
  });

  const columns = useWebhookOverviewTableColumns();

  const handleStatusChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === "all") {
      newParams.delete("status");
    } else {
      newParams.set("status", value);
    }
    // Reset to page 1 when changing filters
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const handleRowClick = (item: RecruitNowWebhookOverviewItem) => {
    navigate(
      `/${ROUTES.clients}/${item.clientId}/${ROUTES.integrations}/${encodeSlug("Cockpit")}/${ModuleName.JobSource}`,
    );
  };

  return (
    <div className="space-y-6 mt-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <TableToolbar
          onSearchChange={setSearchValue}
          searchValue={searchValue}
          searchPlaceholder={t("common.search")}
        />
        <Select
          value={statusFilter !== null ? String(statusFilter) : "all"}
          onValueChange={handleStatusChange}
        >
          <SelectTrigger className="w-48">
            <SelectValue
              placeholder={t("webhooks.recruitnow.filterByStatus")}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")}</SelectItem>
            <SelectItem value={String(WebhookStatus.NoRegistration)}>
              {t("webhooks.recruitnow.status.noRegistration")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        isLoading={isLoading || isFetching}
        onSort={handleSort}
        onRowClick={handleRowClick}
        orderBy={orderBy ?? undefined}
        descending={descending ?? undefined}
        page={page}
        pageSize={pageSize}
        totalRecords={data?.totalCount ?? 0}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        getRowId={(item) => item.clientId}
        emptyState={{
          title: t("common.noData"),
        }}
      />
    </div>
  );
}
